import { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { FormRegion } from "../../components/formregion";
import { formValidator } from "./datacorrectionpageformvalidator";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import {
  loadDataGrid,
  setFormValue,
  setPageValue,
} from "../../redux/slices/datacorrectionpageslice";
import QueryBuilder from "react-querybuilder";
import { CollapsableFormRegion } from "../../components/collapsableformregion";
import { QueryValueEditor } from "../../components/querybuilder/queryvalueeditor";
import { getQueryOperators } from "../../components/querybuilder/queryoperators";
import { AggregateFunction } from "../../lib/aggregatefunctions";
import {
  FormDropDownList,
  FormDropDownListById,
  FormInput,
} from "../../forms/form-components";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { TableValueList } from "./tablevaluelist";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const DataCorrectionPageForm = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.dataCorrectionPage);
  const updateDirtyState = props.updateDirtyState;
  const currentDataset = pageState.currentDataset;
  const { t } = useTranslation();

  let fields = currentDataset.Lookups.Fields;
  let users = pageState.formValues.Viewers;
  let groupByFields = pageState.formValues.GroupedFields;

  let aggregateFunctionId = pageState.formValues.AggregateFunctionId;
  let aggregateFunction =
    aggregateFunctionId == 0
      ? null
      : AggregateFunction.getById(aggregateFunctionId);

  fields = fields.filter((e) => {
    if (aggregateFunction == null) return e.Id == null;
    else
      return (
        e.Id == null ||
        aggregateFunction.getReturnTypeFromField(e.DataTypeId) == 2
      );
  });

  const getOperators = (fieldName) => {
    let field = currentDataset.Where[fieldName];
    if (!field) return [];

    return getQueryOperators(field.DataTypeId, field.IsLookup);
  };

  let isGrouped = groupByFields.find((e) => e.Checked == true);

  let userIsNotified = users.find((e) => e.Checked == true);

  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  const sampleData = pageState.formValues.SampleData;
  let columns = null;

  //#endregion

  if (sampleData && sampleData.length > 0) {
    columns = Object.keys(sampleData[0]).map((key) => ({
      field: key,
      title: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize title
      width: 150, // Explicit width for each column
    }));
  }

  const numberOfRows = 100;
  const numberOfColumns = currentDataset?.Fields?.length;
  const getData = (skip, take) => {
    const pages = [];
    for (let r = skip + 1; r <= skip + take && r <= numberOfRows; r++) {
      const row = {};
      for (let c = 1; c <= numberOfColumns; c++) {
        row["Field-" + c] = "R" + r + ":C" + c;
      }
      pages.push(row);
    }
    return pages;
  };
  const data = getData(0, numberOfRows);

  const [page, setPage] = useState({
    skip: 0,
    take: 20,
  });
  const pageChange = (event) => {
    setPage(event.page);
  };

  const DataCell = (props) => {
    const value = props.dataItem[props.field];

    return <td title={value}>{value}</td>;
  };

  return (
    <Fragment>
      <Form
        validator={formValidator}
        key={pageState.formKey}
        initialValues={{ ...pageState.formInitialValues }}
        render={(formRenderProps) => {
          return (
            <FormElement horizontal={false}>
              <CollapsableFormRegion
                className="form-region-filter"
                title={t("datacorrection.filter")}
                open={pageState.formValues.filterOpen}
                onToggle={(e) =>
                  dispatch(
                    setFormValue({
                      id: "filterOpen",
                      value: !pageState.formValues.filterOpen,
                    }),
                  )
                }
              >
                <QueryBuilder
                  query={pageState.formValues.WhereObject}
                  fields={pageState.currentDataset.Where}
                  getOperators={getOperators}
                  onQueryChange={(q) => {
                    dispatch(setFormValue({ id: "WhereObject", value: q }));
                  }}
                  controlElements={{
                    valueEditor: (editorProps) => (
                      <QueryValueEditor
                        {...editorProps}
                        handleSaveKey={props.onKeyChange}
                      />
                    ),
                  }}
                  showCloneButtons={true}
                />
              </CollapsableFormRegion>
              <CollapsableFormRegion
                className="form-region-group-by"
                title={t("datacorrection.group")}
                open={pageState.formValues.groupByOpen}
                onToggle={(e) =>
                  dispatch(
                    setFormValue({
                      id: "groupByOpen",
                      value: !pageState.formValues.groupByOpen,
                    }),
                  )
                }
              >
                <MultiSelect
                  data={groupByFields}
                  dataItemKey="Id"
                  textField="Title"
                  value={groupByFields.filter((e) => e.Checked == true)}
                  onChange={props.onGroupedFieldsCheck}
                  style={{
                    width: "100%",
                    marginTop: 4,
                  }}
                  placeholder={t("datacorrection.select1")}
                />
              </CollapsableFormRegion>

              <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                <Button
                  onClick={props.onLoadDataGrid}
                  style={{ marginTop: 20 }}
                >
                  {t("datacorrection.view")}
                </Button>
              </div>

              {pageState.formValues.showEditSection && (
                <Fragment>
                  <CollapsableFormRegion
                    className="form-region-group-by"
                    title={t("datacorrection.editfields")}
                    open={pageState.formValues.updateSection}
                    onToggle={() =>
                      dispatch(
                        setFormValue({
                          id: "updateSection",
                          value: !pageState.formValues.updateSection,
                        }),
                      )
                    }
                  >
                    <div>
                      <TableValueList
                        formRenderProps={formRenderProps}
                        onToggleViewMode={(e) => {
                          let viewMode = !pageState.formValues.ViewMode;

                          dispatch(
                            setFormValue({ id: "ViewMode", value: viewMode }),
                          );
                        }}
                        onFieldRemove={props.onFieldRemove}
                        onOpenFieldsDialog={props.onOpenFieldsDialog}
                        onCloseFieldsDialog={props.onCloseFieldsDialog}
                        onFieldCheck={props.onFieldCheck}
                        onSaveFieldsDialog={props.onSaveFieldsDialog}
                        onFieldDragStart={props.onFieldDragStart}
                        onFieldDragOver={props.onFieldDragOver}
                        onFieldDrop={props.onFieldDrop}
                        onNewValueChange={props.onNewValueChange}
                        onFieldsClear={props.onFieldsClear}
                        onFieldTitleChange={props.onFieldTitleChange}
                        onFieldGroupingChange={props.onFieldGroupingChange}
                        onToggleEquation={props.onToggleEquation}
                        onFieldSortDirectionChange={
                          props.onFieldSortDirectionChange
                        }
                        onOperatorChange={props.onOperatorChange}
                        onFieldToUseChange={props.onFieldToUseChange}
                      />
                    </div>
                  </CollapsableFormRegion>

                  <CollapsableFormRegion
                    className="form-region-group-by"
                    title={t("datacorrection.affect")}
                    open={pageState.formValues.gridSection}
                    onToggle={() =>
                      dispatch(
                        setFormValue({
                          id: "gridSection",
                          value: !pageState.formValues.gridSection,
                        }),
                      )
                    }
                  >
                    <div
                      className="k-form-field"
                      style={{
                        width: "300px",
                        marginRight: "20px",
                        marginBottom: "30px",
                      }}
                    >
                      <label className="k-label k-text-disabled">
                        {t("datacorrection.numberaffect")}
                      </label>
                      <div className="k-form-field-wrap">
                        <input
                          aria-describedby=" "
                          aria-disabled="true"
                          disabled
                          className="k-input k-input-md k-rounded-md k-input-solid"
                          value={pageState.formValues.RowsAffected}
                        />
                      </div>
                    </div>
                    {sampleData && sampleData.length > 0 ? (
                      <div>
                        <label className="k-label k-text-disabled">
                          {t("datacorrection.grid")}
                        </label>
                        <Grid
                          scrollable="virtual"
                          //columnVirtualization={true}
                          resizable={true}
                          style={{ height: "400px" }}
                          data={sampleData.slice(
                            page.skip,
                            page.skip + page.take,
                          )}
                          skip={page.skip}
                          take={page.take}
                          total={numberOfRows}
                          onPageChange={pageChange}
                        >
                          {columns
                            .filter((col) => col.title !== "_Id_") // Remove _id_ column
                            .map((col, idx) => (
                              <GridColumn
                                key={idx}
                                field={col.field}
                                title={col.title}
                                width={col.width || 150}
                                cell={DataCell}
                              />
                            ))}
                        </Grid>
                      </div>
                    ) : (
                      <p>{t("datacorrection.nodata")}</p>
                    )}
                  </CollapsableFormRegion>
                </Fragment>
              )}
            </FormElement>
          );
        }}
      />
      <style>
        {`             
                    .datacorrection-page-form .separator
                    {
                        width: 20px;
                    }

                    .datacorrection-page-form .k-chip-list
                    {
                        padding: 6px;
                    }

                    .datacorrection-page-form .k-chip-list .k-chip
                    {
                        line-height: 1.25;
                        margin-right: 4px;
                    }

                    .datacorrection-page-form > .content
                    {
                        padding: 0px;
                        background-color: ${currentTheme.componentBg};
                        align-items: flex-start;
                    }

                    .datacorrection-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/              
                        display: flex;
                        flex-direction: column;
                        align-items: stretch;
                        justify-content: space-between;
                        padding: 0 12px 22px 16px;/*keep 12px in right and bottom so that when minimizing we keep a white region to the right and bottom; also keep 8px on the left*/
                        max-width: 1100px;
                        width:100%;
                    }

                    /* main region */
                    .datacorrection-page-form .form-region-main
                    {
                        margin-top: 8px!important;
                        border-radius: 4px;
                        padding: 8px 0px 0px 0px;
                    }

                    .datacorrection-page-form .form-region-main .header
                    {
                        padding: 4px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                    .datacorrection-page-form .form-region-main .header .right-line
                    {
                        display: none;
                    }

                    .datacorrection-page-form .form-region-main .content
                    {
                        padding-left: 12px;
                    }

                    .datacorrection-page-form .form-region-main .form-region-root
                    {
                    }

                    .datacorrection-page-form .form-region-main > .form-region-root .content div:first-child div:first-child .k-form-field:nth-child(1)
                    {
                        /*title*/
                        /*let title NOT go down by 26px as other fields*/
                        margin-top: 6px!important;
                    }

                    .datacorrection-page-form .form-region-main > .form-region-root .content div:first-child div:first-child .k-form-field:nth-child(2)
                    {
                        /*isactive*/
                        margin-top: 10px!important;
                    }

                    .datacorrection-page-form .form-region-main > .form-region-root .content div:first-child div:nth-child(3) .k-form-field:first-child
                    {
                        /*description*/
                        margin-top: 6px;
                    }                    

                    /*misc*/
                    .datacorrection-page-form .form-region-misc
                    {
                        margin-top: 28px!important;
                        border-radius: 4px;
                        padding: 8px 0px 0px 0px;
                    }

                    .datacorrection-page-form .form-region-misc .header
                    {
                        padding: 4px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                    .datacorrection-page-form .form-region-misc .header .right-line
                    {
                        display: none;
                    }

                    .datacorrection-page-form .form-region-misc .content
                    {
                        padding-left: 12px;
                        margin-top: 12px;
                    }

                    .datacorrection-page-form .form-region-misc .form-region-root
                    {
                    }

                    .datacorrection-page-form .form-region-misc .k-form-field
                    {
                        /*let title NOT go down by 26px as other fields*/
                        margin-top:0 !important;
                    }

                    .datacorrection-page-form .form-region-misc .url .k-form-field
                    {
                        flex:1;
                    }
                                    

                    /* expression region */
                    .datacorrection-page-form .form-region-expression
                    {
                        margin-top: 28px!important;
                        border-radius: 4px;
                        padding: 8px 0px 0px 0px;
                    }

                    .datacorrection-page-form .form-region-expression .header
                    {
                        padding: 4px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                    .datacorrection-page-form .form-region-expression .header .right-line
                    {
                        display: none;
                    }

                    .datacorrection-page-form .form-region-expression .content
                    {
                        padding-left: 12px;
                        margin-top: 12px;
                    }

                    .datacorrection-page-form .form-region-expression .form-region-root
                    {
                    }

                    .datacorrection-page-form .form-region-expression .k-form-field
                    {
                        /*let title NOT go down by 26px as other fields*/
                        margin-top:0 !important;
                    }

                    .datacorrection-page-form .form-region-expression .aggregate-function .k-form-field:nth-child(3),
                    .datacorrection-page-form .form-region-expression .interpretation .k-form-field
                    {
                        flex: 1;
                        width: 100%;
                    }
                    

                    /*group by section*/
                    .datacorrection-page-form .form-region-group-by
                    {
                        margin-top: 36px!important;
                        border-radius: 4px;*/
                        padding: 8px 0px 8px 0px;
                    }

                    .datacorrection-page-form .form-region-group-by .header
                    {
                        padding: 4px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                  
                   /*filter section*/
                    .datacorrection-page-form .form-region-filter
                    {
                        margin-top: 36px!important;
                        border-radius: 4px;*/
                        padding: 8px 0px 8px 0px;
                    }

                    .datacorrection-page-form .form-region-filter .header
                    {
                        padding: 4px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                    .datacorrection-page-form .form-region-filter .header .right-line
                    {
                        display: none;
                    }

                    .datacorrection-page-form .form-region-filter .content
                    {
                        padding-left: 12px;
                        margin-top: 12px;
                    }
                    
                    .datacorrection-page-form .form-region-filter .form-region-root
                    {
                    }       
                    

                    /*query builder*/
                    .datacorrection-page-form .form-region-filter .ruleGroup, .datacorrection-page-form .form-region-filter .ruleGroup-combinators,
                    .datacorrection-page-form .form-region-filter .ruleGroup-addRule, .datacorrection-page-form .form-region-filter .ruleGroup-addGroup,
                    .datacorrection-page-form .form-region-filter .rule-remove, .datacorrection-page-form .form-region-filter .ruleGroup-remove,
                    .datacorrection-page-form .form-region-filter .rule-fields, .datacorrection-page-form .form-region-filter .rule-operators,
                    .datacorrection-page-form .form-region-filter .rule-value, .datacorrection-page-form .form-region-filter .rule-cloneRule
                    {
                        border-color: ${currentTheme.componentBorderColor};
                        background-color: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};                                               
                    }

                    .datacorrection-page-form .form-region-filter .rule-fields:focus, .datacorrection-page-form .form-region-filter .rule-operators:focus,
                    .datacorrection-page-form .form-region-filter .rule-value:focus
                    {
                        outline: none
                    }    
                    
                    .datacorrection-page .form-region-filter .rule-remove, .datacorrection-page .form-region-filter .ruleGroup-remove
                    {
                        color: ${currentTheme.error};
                        padding: 8px 10px 7px 10px;
                    }

                  .datacorrection-page .form-region-root .k-grid td {
                    white-space: nowrap;       /* Prevent line break */
                    overflow: hidden;          /* Hide overflow */
                    text-overflow: ellipsis;   /* Add "..." for overflow */
                    max-width:150px;
                  }
                `}
      </style>
    </Fragment>
  );
};
